import { useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, Clock, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getStageColor } from "@/lib/stageColors";
import { getStageLabel } from "@/lib/stageLabel";
import { festivalDayIso, groupByStage, horizontalPositionForTime } from "@produce/shared";
import { useProducerTimeFormatter } from "@/hooks/useTimeFormatter";
import { useCurrentTimeMarker } from "@/hooks/useCurrentTimeMarker";
import { format } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface SchedulePreviewCardProps {
  productionId: string;
  festivalDateStart: string;
  festivalDateEnd?: string | null;
}

export const SchedulePreviewCard = ({
  productionId,
  festivalDateStart,
  festivalDateEnd,
}: SchedulePreviewCardProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const formatTime = useProducerTimeFormatter();
  const today = festivalDayIso();
  const start = festivalDateStart.split("T")[0];
  const end = festivalDateEnd?.split("T")[0] || start;

  const selectedDay = today >= start && today <= end ? today : today < start ? start : end;

  // Fetch ALL lineup for the selected day (no time filtering)
  const { data: lineup } = useQuery({
    queryKey: ["lineup-preview", productionId, selectedDay],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lineup")
        .select("*, briefing_stages(label)")
        .eq("briefing_id", productionId)
        .eq("event_date", selectedDay)
        .order("set_start", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!productionId && !!selectedDay,
  });

  // Calculate timeline range from actual lineup data
  const timeRange = (() => {
    if (!lineup || lineup.length === 0) {
      return { startHour: 12, endHour: 18 };
    }

    let earliestMin = Infinity;
    let latestMin = -Infinity;

    lineup.forEach((artist) => {
      const [startH, startM] = artist.set_start.split(":").map(Number);
      const [endH, endM] = artist.set_end.split(":").map(Number);
      const startMin = startH * 60 + startM;
      const endMin = endH * 60 + endM;

      if (startMin < earliestMin) earliestMin = startMin;
      if (endMin > latestMin) latestMin = endMin;
    });

    // Round to full hours
    const startHour = Math.floor(earliestMin / 60);
    const endHour = Math.ceil(latestMin / 60);

    return { startHour, endHour };
  })();

  const { startHour, endHour } = timeRange;
  const totalHours = endHour - startHour;

  // Fixed width per hour for scrollable timeline
  const hourWidth = 80;
  const labelWidth = 90;
  const timelineWidth = totalHours * hourWidth;

  // Convert time string to pixel position. Same helper as the schedule
  // creator / read-only timeline, so blocks and marker share one definition
  // of "where does this time sit" — on a plain same-day axis here.
  const timeToPosition = (timeStr: string) =>
    horizontalPositionForTime(timeStr, startHour, hourWidth, { afterMidnight: false });

  const isToday = selectedDay === today;

  // Live "now" marker (shared with the schedule creator / read-only timeline).
  // A plain same-day axis here, so after-midnight times are not rolled forward.
  const currentTimeMarker = useCurrentTimeMarker({
    startHour,
    pixelsPerHour: hourWidth,
    spanHours: totalHours,
    enabled: isToday,
    afterMidnight: false,
  });

  // Auto-scroll to current time when the card gets a (new) day's lineup — not
  // on every minute tick, which would keep yanking the producer's own
  // horizontal scroll back. The position is read through a ref so the marker
  // moving does not re-trigger the effect.
  const markerPositionRef = useRef(currentTimeMarker.position);
  markerPositionRef.current = currentTimeMarker.position;
  useEffect(() => {
    if (!scrollContainerRef.current || !lineup || lineup.length === 0) return;
    // Scroll to show current time roughly in the center
    scrollContainerRef.current.scrollLeft = Math.max(0, markerPositionRef.current - 100);
  }, [lineup, startHour]);

  // Stage groups via shared helper: FK-first (lineup.stage_id),
  // stage_name fallback. Rows is the lineup-per-stage list we use
  // below to render the artist blocks.
  const stageGroups = groupByStage(lineup ?? []);
  const stages = stageGroups.map((g) => ({ key: g.key, stageId: g.stageId, label: g.label }));

  const showCurrentMarker = currentTimeMarker.visible;

  return (
    <TooltipProvider>
      <Card className="flex h-full flex-col border-border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold">
            <Clock className="h-5 w-5" />
            Schedule
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {format(new Date(selectedDay), "MMM d, yyyy")}
            </span>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/briefings/${productionId}/edit?step=5&shortcut=true`}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Edit
              </Link>
            </Button>
          </div>
        </div>

        {/* Scrollable Timeline Container */}
        <div className="min-h-0 flex-1 overflow-hidden">
          {!lineup || lineup.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No schedule data available
            </div>
          ) : (
            <div className="flex h-full">
              {/* Sticky Stage Labels Column */}
              <div className="flex-shrink-0" style={{ width: labelWidth }}>
                {/* Empty space for hour labels */}
                <div className="mb-1 h-5" />

                {/* Stage labels */}
                <div className="overflow-hidden" style={{ maxHeight: "calc(100% - 24px)" }}>
                  {stages.map((stage) => {
                    const stageColor = getStageColor(stage.label);
                    return (
                      <div key={stage.key} className="mb-1 flex h-9 items-center gap-2">
                        <div
                          className="h-6 w-1 flex-shrink-0 rounded-full"
                          style={{ backgroundColor: stageColor }}
                        />
                        <div className="truncate text-xs font-medium text-foreground">
                          {stage.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Scrollable Timeline Area */}
              <div
                ref={scrollContainerRef}
                className="flex-1 overflow-x-auto overflow-y-auto"
                style={{ maxHeight: "100%" }}
              >
                <div className="relative" style={{ width: timelineWidth, minWidth: "100%" }}>
                  {/* Hour labels */}
                  <div className="sticky top-0 z-10 mb-1 flex h-5 bg-card">
                    {Array.from({ length: totalHours + 1 }).map((_, i) => {
                      const hour = startHour + i;
                      return (
                        <div
                          key={hour}
                          className="absolute text-xs font-medium text-muted-foreground"
                          style={{
                            left: i * hourWidth,
                            transform: "translateX(-50%)",
                          }}
                        >
                          {formatTime(`${hour.toString().padStart(2, "0")}:00`)}
                        </div>
                      );
                    })}
                  </div>

                  {/* Stage rows */}
                  <div className="relative">
                    {stageGroups.map((group) => {
                      const stageArtists = group.rows;

                      return (
                        <div key={group.key} className="relative mb-1 h-9 rounded bg-muted/20">
                          {/* Grid lines */}
                          {Array.from({ length: totalHours + 1 }).map((_, i) => (
                            <div
                              key={i}
                              className={`absolute bottom-0 top-0 border-l ${
                                i % 1 === 0 ? "border-border/40" : "border-border/20"
                              }`}
                              style={{ left: i * hourWidth }}
                            />
                          ))}

                          {/* Artist blocks (glass effect) */}
                          {stageArtists.map((artist) => {
                            const left = timeToPosition(artist.set_start);
                            const right = timeToPosition(artist.set_end);
                            const width = right - left;

                            return (
                              <Tooltip key={artist.id}>
                                <TooltipTrigger asChild>
                                  <div
                                    className="absolute top-0 flex h-full cursor-pointer items-center overflow-hidden rounded border border-white/30 px-1"
                                    style={{
                                      left,
                                      width: Math.max(width, 20),
                                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                                      backdropFilter: "blur(4px)",
                                    }}
                                  >
                                    <span className="truncate text-[10px] font-medium text-white drop-shadow-md">
                                      {artist.artist_name}
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="text-xs">
                                    <div className="font-semibold">{artist.artist_name}</div>
                                    <div className="text-muted-foreground">
                                      {getStageLabel(artist)} • {formatTime(artist.set_start)} -{" "}
                                      {formatTime(artist.set_end)}
                                    </div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </div>
                      );
                    })}

                    {/* Current time marker */}
                    {showCurrentMarker && (
                      <div
                        className="pointer-events-none absolute top-0 w-[2px] bg-[#d1ff38]"
                        style={{
                          left: currentTimeMarker.position,
                          height: `${stages.length * 40}px`,
                          zIndex: 30,
                        }}
                      >
                        <div className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold text-[#d1ff38]">
                          Now
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <Button variant="ghost" className="mt-auto w-full text-primary hover:text-primary" asChild>
          <Link to={`/productions/${productionId}/schedule`}>
            Open full schedule
            <ExternalLink className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </Card>
    </TooltipProvider>
  );
};
